/**
 * EmailSharingSection — White-Label → Email → "Organizations under this one".
 *
 * An organization's sender serves the organizations under it only once it
 * SHARES it — the switch here, OFF until someone turns it on, and never past
 * the company (an organization in no company, or above one, shares with no
 * one); its email design reaches those that wear the same brand, whatever
 * the switch says (shared/services/email_inheritance.py). A super admin can
 * also apply both to all organizations under this one at once: a preview
 * first says whose mail changes and who has settings of their own (kept unless "Also replace their own settings" is ticked — the
 * box names them), then each organization is applied all-or-nothing and the
 * apply is recorded in the history.
 *
 * Applying never copies anything: the organizations under this one inherit,
 * so no SMTP password leaves the organization where it was entered. Super
 * admin only — the backend enforces it on every route.
 */
import { useCallback, useEffect, useState } from 'react';
import { Button, Spin, Switch, message as antdMessage } from 'antd';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';
import { formatExactTime } from '@/utils/relativeTime';
import { bidiIsolate } from '@/utils/bidi';
import { ConnectorDialog, PrimaryAction } from '@/modules/chatbots/components/ConnectorDialog';

interface Sharing {
  tenant_name: string;
  shared: boolean;
  chosen: boolean;
  is_root: boolean;
  in_company: boolean;
  has_own: boolean;
  organizations_under: number;
  following: { design: number; sender: number };
}

interface Change { design: boolean; sender: boolean }

interface PreviewOrg {
  tenant_id: string;
  tenant_name: string;
  depth: number;
  own: { design_fields: string[]; sender: 'active' | 'test_required' | null };
  has_own: boolean;
  keep: Change;
  replace: Change;
}

interface Preview {
  source: { tenant_id: string; tenant_name: string; shared: boolean; in_company: boolean };
  organizations: PreviewOrg[];
  counts: { total: number; own: number; changes_keep: number; changes_replace: number };
}

interface Run {
  run_id: string;
  at: string | null;
  actor_name: string | null;
  replaced_own: boolean;
  changed: number;
  kept: number;
  failed: number;
}

export default function EmailSharingSection({
  tenantId, revision,
}: {
  tenantId: string;
  /** Changes when this organization's own email settings were saved. */
  revision: string;
}) {
  const { t } = useLanguage();
  const [sharing, setSharing] = useState<Sharing | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [dialog, setDialog] = useState<'apply' | 'history' | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/super-admin/tenants/${tenantId}/email-sharing`);
      setSharing(res.data);
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, [tenantId]);

  useEffect(() => { setSharing(null); load(); }, [load, revision]);

  if (loadError) {
    return <div className="wl-field-hint" role="alert">{t('whiteLabel.emailSharing.loadFailed')}</div>;
  }
  if (!sharing) {
    return <div className="wl-email-sending-loading"><Spin /></div>;
  }

  const toggle = async (shared: boolean) => {
    setSwitching(true);
    try {
      const res = await api.put(`/super-admin/tenants/${tenantId}/email-sharing`, { shared });
      setSharing(res.data);
    } catch (err: unknown) {
      const code = (err as { response?: { data?: { error_code?: string } } })?.response?.data?.error_code;
      antdMessage.error(t(code === 'sharing_outside_company'
        ? 'whiteLabel.emailSharing.outsideCompany' : 'whiteLabel.emailSharing.saveFailed'));
    } finally {
      setSwitching(false);
    }
  };

  const none = sharing.organizations_under === 0;
  let state: string;
  if (none) state = t('whiteLabel.emailSharing.noneUnder');
  else if (!sharing.in_company) state = t('whiteLabel.emailSharing.outsideCompany');
  // A top-level organization has nothing above it to follow.
  else if (!sharing.has_own) {
    state = t(sharing.is_root ? 'whiteLabel.emailSharing.nothingOwnTop' : 'whiteLabel.emailSharing.nothingOwn');
  }
  else if (sharing.shared) {
    state = t('whiteLabel.emailSharing.followingNow', {
      design: sharing.following.design, sender: sharing.following.sender, total: sharing.organizations_under,
    });
  } else state = t('whiteLabel.emailSharing.notShared');

  return (
    <section className="wl-section" data-testid="wl-email-sharing">
      <header className="wl-section-header">
        <h4 className="wl-section-title">{t('whiteLabel.emailSharing.title')}</h4>
        <p className="wl-section-subtitle">{t('whiteLabel.emailSharing.subtitle')}</p>
      </header>
      <div className="wl-section-body">
        <div className="wl-field">
          <label className="wl-email-sharing-switch">
            <Switch
              checked={sharing.shared}
              loading={switching}
              // Outside a company there is no one to share with; a stored ON
              // can still be switched off.
              disabled={!sharing.in_company && !sharing.shared}
              onChange={toggle}
              data-testid="wl-email-sharing-switch"
            />
            <span>{t('whiteLabel.emailSharing.switch')}</span>
          </label>
          <div className="wl-field-hint">{t('whiteLabel.emailSharing.switchHint')}</div>
          <div className="wl-field-hint" data-testid="wl-email-sharing-state">{state}</div>
        </div>
        <div className="wl-email-sending-actions">
          <Button
            onClick={() => setDialog('apply')}
            disabled={none}
            data-testid="wl-email-apply-open"
          >
            {t('whiteLabel.emailSharing.applyButton')}
          </Button>
          <Button type="link" onClick={() => setDialog('history')} data-testid="wl-email-apply-history-open">
            {t('whiteLabel.emailSharing.history')}
          </Button>
        </div>
      </div>
      {dialog === 'apply' && (
        <ApplyDialog
          tenantId={tenantId}
          tenantName={sharing.tenant_name}
          onClose={() => setDialog(null)}
          onApplied={(next) => { setSharing(next); setDialog(null); }}
        />
      )}
      {dialog === 'history' && (
        <HistoryDialog tenantId={tenantId} tenantName={sharing.tenant_name} onClose={() => setDialog(null)} />
      )}
    </section>
  );
}

function ApplyDialog({
  tenantId, tenantName, onClose, onApplied,
}: {
  tenantId: string;
  tenantName: string;
  onClose: () => void;
  onApplied: (sharing: Sharing) => void;
}) {
  const { t } = useLanguage();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [replace, setReplace] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.post(`/super-admin/tenants/${tenantId}/email-settings/apply-to-descendants`, { dry_run: true })
      .then(res => { if (!cancelled) setPreview(res.data); })
      .catch(() => { if (!cancelled) setError(t('whiteLabel.emailSharing.apply.loadFailed')); });
    return () => { cancelled = true; };
  }, [tenantId, t]);

  const how = replace ? 'replace' : 'keep';
  const orgs = preview?.organizations ?? [];
  const changing = orgs.filter(o => o[how].design || o[how].sender);
  const own = orgs.filter(o => o.has_own);
  const ownSenders = own.filter(o => o.own.sender !== null).length;
  const count = replace ? preview?.counts.changes_replace ?? 0 : preview?.counts.changes_keep ?? 0;

  const apply = async () => {
    setApplying(true);
    setError(null);
    try {
      const res = await api.post(`/super-admin/tenants/${tenantId}/email-settings/apply-to-descendants`, { replace });
      const run: Run = res.data.run;
      antdMessage.success(run.failed
        ? t('whiteLabel.emailSharing.apply.doneWithFailures', { count: run.changed, failed: run.failed })
        : t('whiteLabel.emailSharing.apply.done', { count: run.changed }));
      onApplied(res.data.sharing);
    } catch {
      setError(t('whiteLabel.emailSharing.apply.failed'));
    } finally {
      setApplying(false);
    }
  };

  const what = (change: Change) => (
    change.design && change.sender ? t('whiteLabel.emailSharing.apply.designAndSender')
      : change.design ? t('whiteLabel.emailSharing.apply.designOnly') : t('whiteLabel.emailSharing.apply.senderOnly')
  );
  const ownWhat = (org: PreviewOrg) => [
    org.own.design_fields.length ? t('whiteLabel.emailSharing.apply.ownDesign') : '',
    org.own.sender ? t('whiteLabel.emailSharing.apply.ownSender') : '',
  ].filter(Boolean).join(' · ');

  return (
    <ConnectorDialog
      title={t('whiteLabel.emailSharing.apply.title')}
      subtitle={t('whiteLabel.emailSharing.apply.subtitle', { org: bidiIsolate(tenantName) })}
      size="medium"
      onClose={onClose}
      note={error}
      noteTone="error"
      testId="wl-email-apply-dialog"
      actions={<>
        <button type="button" className="btn-cancel" onClick={onClose}>{t('common.cancel')}</button>
        <PrimaryAction
          label={count ? t('whiteLabel.emailSharing.apply.confirm', { count }) : t('whiteLabel.emailSharing.apply.nothing')}
          busy={applying}
          disabled={!preview || !count}
          onClick={apply}
          testId="wl-email-apply-confirm"
        />
      </>}
    >
      {!preview ? (
        !error && <div className="wl-email-sending-loading"><Spin /></div>
      ) : (
        <div className="wl-email-apply">
          <p>{t('whiteLabel.emailSharing.apply.lead')}</p>
          {!preview.source.shared && preview.source.in_company && (
            <p>{t('whiteLabel.emailSharing.apply.turnsSharingOn')}</p>
          )}

          <h3 className="wl-email-apply-head">
            {t('whiteLabel.emailSharing.apply.changing', { count: changing.length })}
          </h3>
          {changing.length ? (
            <ul className="wl-email-apply-list" data-testid="wl-email-apply-changing">
              {changing.map(o => (
                <li key={o.tenant_id}>
                  <span dir="auto">{o.tenant_name}</span>
                  <span className="wl-email-apply-what">{what(o[how])}</span>
                </li>
              ))}
            </ul>
          ) : <p className="wl-field-hint">{t('whiteLabel.emailSharing.apply.noneChanging')}</p>}

          {own.length > 0 && (<>
            <h3 className="wl-email-apply-head">
              {replace
                ? t('whiteLabel.emailSharing.apply.ownReplaced', { count: own.length })
                : t('whiteLabel.emailSharing.apply.ownKept', { count: own.length })}
            </h3>
            <label className={`wl-email-apply-force ${replace ? 'is-on' : ''}`}>
              <input
                type="checkbox"
                checked={replace}
                onChange={e => setReplace(e.target.checked)}
                data-testid="wl-email-apply-replace"
              />
              <span>
                <strong>{t('whiteLabel.emailSharing.apply.replaceLabel')}</strong>
                <ul className="wl-email-apply-list" data-testid="wl-email-apply-own">
                  {own.map(o => (
                    <li key={o.tenant_id}>
                      <span dir="auto">{o.tenant_name}</span>
                      <span className="wl-email-apply-what">{ownWhat(o)}</span>
                    </li>
                  ))}
                </ul>
                {replace && ownSenders > 0 && (
                  <span className="wl-field-hint">
                    {t('whiteLabel.emailSharing.apply.replaceSenders', { count: ownSenders })}
                  </span>
                )}
              </span>
            </label>
          </>)}
          <p className="wl-field-hint">{t('whiteLabel.emailSharing.apply.noCopies')}</p>
        </div>
      )}
    </ConnectorDialog>
  );
}

function HistoryDialog({
  tenantId, tenantName, onClose,
}: { tenantId: string; tenantName: string; onClose: () => void }) {
  const { t } = useLanguage();
  const [runs, setRuns] = useState<Run[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/super-admin/tenants/${tenantId}/email-settings/apply-history`)
      .then(res => { if (!cancelled) setRuns(res.data.runs); })
      .catch(() => { if (!cancelled) setError(t('whiteLabel.emailSharing.historyDialog.loadFailed')); });
    return () => { cancelled = true; };
  }, [tenantId, t]);

  return (
    <ConnectorDialog
      title={t('whiteLabel.emailSharing.historyDialog.title')}
      subtitle={t('whiteLabel.emailSharing.historyDialog.subtitle', { org: bidiIsolate(tenantName) })}
      size="medium"
      onClose={onClose}
      note={error}
      noteTone="error"
      testId="wl-email-apply-history"
      actions={<button type="button" className="btn-cancel" onClick={onClose}>{t('common.close')}</button>}
    >
      {!runs ? (
        !error && <div className="wl-email-sending-loading"><Spin /></div>
      ) : runs.length === 0 ? (
        <p className="wl-field-hint">{t('whiteLabel.emailSharing.historyDialog.none')}</p>
      ) : (
        <ul className="wl-email-apply-list wl-email-apply-history">
          {runs.map(run => (
            <li key={run.run_id}>
              <span>
                {t('whiteLabel.emailSharing.historyDialog.entry', {
                  when: formatExactTime(run.at ?? undefined),
                  who: bidiIsolate(run.actor_name || t('whiteLabel.emailSharing.historyDialog.someone')),
                })}
              </span>
              <span className="wl-email-apply-what">
                {[
                  t('whiteLabel.emailSharing.historyDialog.changed', { count: run.changed }),
                  run.kept ? t('whiteLabel.emailSharing.historyDialog.kept', { count: run.kept }) : '',
                  run.replaced_own ? t('whiteLabel.emailSharing.historyDialog.replaced') : '',
                  run.failed ? t('whiteLabel.emailSharing.historyDialog.failed', { count: run.failed }) : '',
                ].filter(Boolean).join(' · ')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </ConnectorDialog>
  );
}
